import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import TodayPage from './components/TodayPage';
import MyTeamPage from './components/MyTeamPage';
import MyStarPage from './components/MyStarPage';
import SchedulePage from './components/SchedulePage';

const fallbackMatches = [
  {
    id: 'fallback-1',
    home_team: 'United States',
    away_team: 'Mexico',
    kickoff_at: '2026-06-14T13:00:00',
    venue: 'AT&T Stadium',
    location: 'Arlington, USA',
  },
  {
    id: 'fallback-2',
    home_team: 'Argentina',
    away_team: 'Canada',
    kickoff_at: '2026-06-14T16:00:00',
    venue: 'Mercedes-Benz Stadium',
    location: 'Atlanta, USA',
  },
  {
    id: 'fallback-3',
    home_team: 'Brazil',
    away_team: 'France',
    kickoff_at: '2026-06-14T19:00:00',
    venue: 'MetLife Stadium',
    location: 'East Rutherford, USA',
  },
  {
    id: 'fallback-4',
    home_team: 'England',
    away_team: 'Germany',
    kickoff_at: '2026-06-14T22:00:00',
    venue: 'SoFi Stadium',
    location: 'Inglewood, USA',
  },
];

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
const HEARTBEAT_INTERVAL_MS = 15000;

function getVisitorId() {
  const existingVisitorId = window.localStorage.getItem('worldcupVisitorId');

  if (existingVisitorId) {
    return existingVisitorId;
  }

  const visitorId = window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `visitor-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  window.localStorage.setItem('worldcupVisitorId', visitorId);
  return visitorId;
}

function App() {
  const mainContentRef = useRef(null);
  const [matches, setMatches] = useState(fallbackMatches);
  const [loading, setLoading] = useState(true);
  const [onlineCount, setOnlineCount] = useState(0);
  const [activePage, setActivePage] = useState('today');
  const [timeZone, setTimeZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/matches/schedule`);
        const rows = response.data;
        if (Array.isArray(rows) && rows.length > 0) {
          const normalized = rows.map(m => ({
            id: `match-${m.match_no}`,
            matchNo: m.match_no,
            date: m.match_date ? m.match_date.slice(0, 10) : null,
            home_team: m.home_team,
            away_team: m.away_team,
            kickoff_at: m.match_date && m.kickoff_time
              ? `${m.match_date.slice(0, 10)}T${m.kickoff_time}:00-07:00`
              : null,
            venue: m.stadium,
            location: m.city && m.country ? `${m.city}, ${m.country}` : m.city,
            result: m.home_score !== null && m.away_score !== null
              ? `${m.home_team} ${m.home_score}-${m.away_score} ${m.away_team}`
              : '',
            status: m.status,
            home_score: m.home_score,
            away_score: m.away_score,
            stage: m.stage,
            group: m.group_name,
          }));
          setMatches(normalized);
        } else {
          setMatches(fallbackMatches);
        }
      } catch (err) {
        console.log('Using fallback matches');
        setMatches(fallbackMatches);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, []);

  useEffect(() => {
    const visitorId = getVisitorId();

    const sendHeartbeat = async () => {
      try {
        const response = await axios.post(`${API_BASE_URL}/visitors/heartbeat`, { visitorId });
        setOnlineCount(response.data.onlineCount);
      } catch (err) {
        setOnlineCount(0);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        sendHeartbeat();
      }
    };

    const handleBeforeUnload = () => {
      const payload = JSON.stringify({ visitorId });
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon?.(`${API_BASE_URL}/visitors/leave`, blob);
    };

    sendHeartbeat();
    const heartbeatTimerId = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.clearInterval(heartbeatTimerId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    window.requestAnimationFrame(() => {
      mainContentRef.current?.scrollTo({ top: 0, left: 0 });
      window.scrollTo({ top: 0, left: 0 });
    });
  }, [activePage]);

  return (
    <div className="app-container">
      <Header onlineCount={onlineCount} onTimeZoneChange={setTimeZone} />
      <div className="content-shell">
        <Sidebar activePage={activePage} onPageChange={setActivePage} />
        <main className="main-content" ref={mainContentRef}>
          {activePage === 'my-team' ? (
            <MyTeamPage />
          ) : activePage === 'my-star' ? (
            <MyStarPage />
          ) : activePage === 'schedule' ? (
            <SchedulePage />
          ) : (
            <TodayPage
              matches={matches}
              loading={loading}
              timeZone={timeZone}
              onViewSchedule={() => setActivePage('schedule')}
              onExplore={() => setActivePage('my-team')}
            />
          )}
        </main>
      </div>
      <footer className="site-footer">
        <span>
          Copyright © 2026 JustForFunLab. All rights reserved.
          <span className="footer-divider">|</span>
          Contact us: <a href="mailto:chuyuwang310@gmail.com">chuyuwang310@gmail.com</a>,
          {' '}
          <a href="mailto:shijie.zhu2018@outlook.com">shijie.zhu2018@outlook.com</a>
        </span>
      </footer>
    </div>
  );
}

export default App;
